import { useEffect } from 'react';

import qs from 'qs';
import { useLocation } from 'react-router';
import z from 'zod';

import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useStartAPetitionPageData } from 'src/app/pages/startAPetitionV2/pageContext';
import { useQueryParamValue } from 'src/app/shared/hooks/location';
import { useIsStarter } from 'src/app/shared/hooks/session';

export const requiredAiDraftSchema = z.object({
	type: z.string().optional().nullable(),
	petitionDraft: z.object({ ask: z.string(), description: z.string() }),
	recommendedDraft: z.object({ ask: z.string(), description: z.string() }),
});

export const requiredUserDraftSchema = z.object({
	type: z.string().optional().nullable(),
	petitionDraft: z.object({ ask: z.string(), description: z.string() }),
});

export function useRedirectHandler() {
	const { data } = useStartAPetitionPageData();
	const { errorReporter } = useUtilityContext();

	const { pathname, search } = useLocation();
	const pathWithoutStep = pathname.split('/').slice(0, -1).join('/');
	const sourceLocation = useQueryParamValue('source_location');
	const hasStartedFlowParam = search.includes('started_flow');
	const startedFlow = useQueryParamValue('started_flow') === 'true';
	const isStarter = useIsStarter();

	const track = useTracking();
	const navigate = useNavigate();

	useEffect(() => {
		if (startedFlow) return;

		void track('sap_view', { source_location: sourceLocation, is_starter: isStarter });

		const parsedQuery = qs.parse(search, { ignoreQueryPrefix: true });
		const query = qs.stringify({ ...parsedQuery, started_flow: true });

		if (hasStartedFlowParam) {
			navigate(`${pathname}?${query}`, { replace: true, preventScrollReset: true });
			return;
		}

		let redirectToStep: 'intro' | 'create' | 'preview' = 'intro';
		try {
			const schema = data.draft.type === 'user_draft' ? requiredUserDraftSchema : requiredAiDraftSchema;
			const draftResult = schema.safeParse(data.draft);

			if (draftResult.success) {
				redirectToStep = draftResult.data.type === 'user_draft' ? 'create' : 'preview';
			}
		} catch (parseError) {
			void errorReporter.report(
				{
					error: new Error('Failed to parse draft', {
						cause: parseError,
					}),
					params: {
						operation: 'useRedirectHandler',
						draftData: data.draft,
						parseError: parseError instanceof Error ? parseError.message : String(parseError),
					},
				},
				{
					severity: 'error',
				},
			);
		}

		navigate(`${pathWithoutStep}/${redirectToStep}?${query}`, { replace: true, preventScrollReset: true });

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { data: { startedFlow } };
}
